import {
  ChangeEventHandler,
  FocusEventHandler,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Form } from 'react-bootstrap'
import { IAPIErrorFormat, RequestStatus } from '../../common/types'
import { REIT } from '../../state/modules/reits'
import { ComponentLoader } from '../common/Loaders'

interface REITSelectProps {
  onREITChange: (reit?: REIT) => void
  disabled: boolean
  reitList: REIT[]
  selectedREIT?: REIT
  reitsStatus?: RequestStatus
  reitsHTTPErrors?: IAPIErrorFormat
  allowNewValue?: boolean
}

export const REITSelect: FunctionComponent<REITSelectProps> = (props) => {
  const {
    onREITChange,
    disabled,
    reitList,
    selectedREIT,
    reitsStatus,
    reitsHTTPErrors,
    allowNewValue = false,
  } = props

  const [options, setOptions] = useState(reitList)
  const [manualEntry, setManualEntry] = useState<string>('')

  useEffect(() => {
    if (allowNewValue) {
      setOptions([...reitList, { id: -1, name: 'Enter new value...' }])
    }
  }, [reitList, allowNewValue])

  const handleDropdownChange = useMemo<
    ChangeEventHandler<HTMLSelectElement> | undefined
  >(
    () =>
      onREITChange
        ? (e) => {
            const idStr = e.currentTarget.value
            const newId = parseInt(idStr, 10)
            if (isNaN(newId)) throw new Error(`Invalid REIT id: ${idStr}`)
            if (newId === -1) {
              // user is choosing to manually enter a new REIT name
              const newREIT = { id: -1, name: manualEntry }
              onREITChange(newREIT)
            } else {
              const newREIT = reitList.find(
                ({ id: recordId }) => recordId === newId
              )
              onREITChange(newREIT)
            }
          }
        : undefined,
    [reitList, onREITChange, manualEntry]
  )

  const handleManualEntryChange = useMemo<
    FocusEventHandler<HTMLInputElement> | undefined
  >(
    () =>
      onREITChange
        ? (e) => {
            const newName = e.currentTarget.value
            setManualEntry(newName)
            onREITChange({ id: -1, name: newName })
          }
        : undefined,
    [onREITChange]
  )

  return (
    <fieldset>
      <legend className="required">Associated REIT</legend>
      <Form.Group style={{ display: 'flex' }}>
        {reitsStatus === RequestStatus.LOADING ? (
          <ComponentLoader />
        ) : (
          <Form.Control
            name="entity-select"
            as="select"
            value={selectedREIT === undefined ? '' : selectedREIT.id}
            onChange={handleDropdownChange}
            disabled={disabled}
          >
            <option disabled hidden style={{ display: 'none' }} value="" />
            {options
              .map(({ id: recordId, name }) =>
                name === '' || name === undefined ? null : (
                  <option key={recordId} value={recordId}>
                    {name}
                  </option>
                )
              )
              .filter((val) => !!val)}
          </Form.Control>
        )}
        {reitsStatus === RequestStatus.FAILED ? (
          <p>
            <small>{reitsHTTPErrors?.message}</small>
          </p>
        ) : (
          ''
        )}
        {allowNewValue && selectedREIT?.id == -1 && (
          <Form.Control
            type="text"
            placeholder="Enter new REIT name"
            style={{ marginLeft: '20px' }}
            onBlur={handleManualEntryChange}
          />
        )}
      </Form.Group>
    </fieldset>
  )
}
